import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AppContext } from '../App';
import { useFilters } from '../context/FilterContext';
import { TrendingUp, ChevronLeft } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { PAY_TYPE_COLORS, DEFAULT_MULTIPLIERS, getPayTypeColor } from '../utils/payTypeColors';
import { getCached, setCached, CACHE_TTL } from '../utils/cacheUtils';

// Skeleton loader component
const TrendlineSkeleton = () => (
  <div className="animate-pulse space-y-4">
    <div className="h-8 w-64 bg-slate-200 rounded"></div>
    <div className="h-4 w-48 bg-slate-100 rounded"></div>
    <div className="h-[350px] bg-slate-100 rounded-lg"></div>
    <div className="h-[150px] bg-slate-50 rounded-lg mt-4"></div>
  </div>
);

const OvertimeTrendline = ({ onDrillDown }) => {
  const { API, premiumPayTypes } = React.useContext(AppContext);
  const { filters } = useFilters();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewType, setViewType] = useState('monthly');
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [multipliers, setMultipliers] = useState(DEFAULT_MULTIPLIERS);

  // Create stable filter key for dependency
  const filterKey = JSON.stringify({
    start_date: filters?.start_date || null,
    end_date: filters?.end_date || null,
    location: filters?.location || null,
    department: filters?.department || null,
    manager: filters?.manager || null,
    pay_types: filters?.pay_types || []
  });

  const fetchTrendline = async (yearMonth = null) => {
    setLoading(true);
    
    const params = {
      view_type: yearMonth ? 'weekly' : 'monthly',
      start_date: filters?.start_date || undefined,
      end_date: filters?.end_date || undefined,
      location: filters?.location || undefined,
      department: filters?.department || undefined,
      manager: filters?.manager || undefined,
      pay_types: filters?.pay_types && filters.pay_types.length > 0 ? filters.pay_types : undefined
    };
    if (yearMonth) {
      params.year_month = yearMonth;
    }
    
    // DISABLED cache to ensure filters always propagate
    // const cachedData = getCached('trendline', params);
    // if (cachedData) { ... }
    
    try {
      const response = await axios.post(`${API}/analytics/trendline`, params);
      setData(response.data);
      setViewType(response.data.view_type);
      
      // Update multipliers from API response
      if (response.data.multipliers) {
        setMultipliers(response.data.multipliers);
      }
      
      if (yearMonth) {
        setSelectedMonth(yearMonth);
      }
      
      // Cache the result for future use
      setCached('trendline', params, response.data, CACHE_TTL.LONG);
    } catch (error) {
      console.error('Error fetching trendline:', error);
      toast.error('Failed to load trendline data');
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch when global filters change
  useEffect(() => {
    setSelectedMonth(null);
    fetchTrendline();
  }, [API, filterKey]); // Use filterKey as dependency

  const handleBarClick = (data) => {
    if (viewType === 'monthly' && data?.period) {
      // Drill down to weekly
      fetchTrendline(data.period);
    }
  };

  const handleBackToMonthly = () => {
    setSelectedMonth(null);
    fetchTrendline();
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const breakdown = (data.premium_breakdown || []).filter(it => it.hours > 0);
      return (
        <div className="bg-white border border-border rounded-lg shadow-lg p-3 min-w-[220px]">
          <p className="font-semibold text-[#1e3a5f] mb-2">{label}</p>
          <div className="space-y-1 text-sm">
            {breakdown.map(item => (
              <div key={item.pay_type} className="flex justify-between gap-4">
                <span className="text-muted-foreground">{item.pay_type} ({item.multiplier}x):</span>
                <span className="font-medium" style={{ color: getPayTypeColor(item.pay_type) }}>
                  {item.hours?.toFixed(1)}h
                </span>
              </div>
            ))}
            <div className="flex justify-between gap-4 border-t pt-1 mt-1">
              <span className="text-muted-foreground">Total Premium:</span>
              <span className="font-bold">{data.total_premium_hours?.toFixed(1)}h</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Cost:</span>
              <span className="font-medium text-[#4CAF93]">${data.premium_cost?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Employees:</span>
              <span className="font-medium">{data.employee_count}</span>
            </div>
          </div>
          {viewType === 'monthly' && (
            <p className="text-xs text-[#2E5C8A] mt-2 border-t pt-2">Click to see weekly breakdown</p>
          )}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return <TrendlineSkeleton />;
  }

  return data?.data?.length > 0 ? (
    <div className="space-y-4" data-testid="overtime-trendline">
      {/* Header with Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-2">
          {selectedMonth && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToMonthly}
              className="text-[#2E5C8A]"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back to Monthly
            </Button>
          )}
          <div>
            <h3 className="text-lg font-semibold text-[#1e3a5f] flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              {selectedMonth 
                ? `Weekly Breakdown: ${selectedMonth}` 
                : 'Premium Pay Trendline'}
            </h3>
            <p className="text-sm text-muted-foreground">
              {viewType === 'monthly' 
                ? 'Monthly premium hours over time' 
                : 'Weekly overtime and double time hours'}
            </p>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg border border-border p-4"
      >
        <ResponsiveContainer width="100%" height={350}>
          <BarChart 
            data={data.data}
            onClick={(e) => e?.activePayload && handleBarClick(e.activePayload[0]?.payload)}
            style={{ cursor: viewType === 'monthly' ? 'pointer' : 'default' }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E8F0F8" />
            <XAxis 
              dataKey="period" 
              tick={{ fill: '#1e3a5f', fontSize: 12 }}
              tickLine={{ stroke: '#1e3a5f' }}
            />
            <YAxis 
              tick={{ fill: '#1e3a5f', fontSize: 12 }}
              tickLine={{ stroke: '#1e3a5f' }}
              label={{ value: 'Hours', angle: -90, position: 'insideLeft', fill: '#1e3a5f' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {/* Dynamic stacked bars — one per premium pay type from AppPayType.PremiumFlag */}
            {premiumPayTypes.map((pt, idx) => (
              <Bar
                key={pt.pay_type}
                dataKey={(record) => {
                  const item = record?.premium_breakdown?.find(b => b.pay_type === pt.pay_type);
                  return item?.hours || 0;
                }}
                name={`${pt.pay_type} (${pt.multiplier}x)`}
                fill={getPayTypeColor(pt.pay_type)}
                stackId="premium"
                radius={idx === premiumPayTypes.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
        
        {/* Cost Line Chart */}
        <div className="mt-6 pt-4 border-t">
          <h4 className="text-sm font-medium text-[#1e3a5f] mb-3">Premium Pay Cost Trend</h4>
          <ResponsiveContainer width="100%" height={150}>
            <LineChart data={data.data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8F0F8" />
              <XAxis dataKey="period" tick={{ fontSize: 10 }} />
              <YAxis 
                tick={{ fontSize: 10 }}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip 
                formatter={(value) => [`$${value.toLocaleString()}`, 'Cost']}
              />
              <Line 
                type="monotone" 
                dataKey="premium_cost" 
                stroke="#4CAF93" 
                strokeWidth={2}
                dot={{ fill: '#4CAF93', r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </motion.div>
    </div>
  ) : (
    <div className="text-center py-12 text-muted-foreground">
      <TrendingUp className="h-12 w-12 mx-auto mb-3 opacity-20" />
      <p>No trendline data available</p>
    </div>
  );
};

export default OvertimeTrendline;
